import { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Lock, ShieldCheck, LockOpen } from "lucide-react";
import { toast } from "sonner";

/**
 * Package 1B — Locked donor section.
 * Displays until the user completes the step-up unlock. Wraps the underlying
 * donor UI once unlocked. Session token is held in sessionStorage only, so
 * closing the browser tab re-locks.
 */
export default function StepUpUnlockPanel({ stepUp, children }) {
  const [password, setPassword] = useState("");

  if (stepUp.unlocked) {
    return (
      <>
        <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 mb-4"
             data-testid="step-up-unlocked-banner">
          <div className="flex items-center gap-2 text-sm text-emerald-800">
            <LockOpen className="h-4 w-4" />
            Donor Records unlocked — {Math.max(0, Math.round(stepUp.remainingSeconds / 60))} min remaining
          </div>
          <Button size="sm" variant="ghost" onClick={stepUp.lock} data-testid="step-up-lock-btn">
            Lock now
          </Button>
        </div>
        {children}
      </>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const r = await stepUp.unlock(password);
    setPassword("");
    if (!r.ok) {
      if (r.code === "STEP_UP_RATE_LIMITED") {
        toast.error("Too many failed attempts. Please try again in 15 minutes.");
      } else {
        toast.error(r.message || "Password incorrect.");
      }
    }
  };

  return (
    <Card className="border-slate-200" data-testid="step-up-locked-panel">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Lock className="h-5 w-5 text-amber-700" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Donor Records — locked</h2>
            <p className="text-xs text-slate-500">
              Confirm your account password to unlock donor records for the next 15 minutes.
            </p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-slate-600">Account password</label>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={stepUp.loading}
              className="mt-1"
              data-testid="step-up-password-input"
            />
          </div>
          <Button type="submit" disabled={!password || stepUp.loading} className="w-full" data-testid="step-up-submit-btn">
            <ShieldCheck className="h-4 w-4 mr-1" />
            {stepUp.loading ? "Unlocking…" : "Unlock donor records"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
